import { useEffect, useState } from 'react';
import { WATCHDOG_SCHEMA_VERSION } from '../types';
import type { WatchdogPublished } from '../types';
import type { FeedSource } from './useFeed';

type WatchdogFeedState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'schema_mismatch'; found: string; expected: string }
  | { status: 'ok'; feed: WatchdogPublished; source: FeedSource };

/**
 * Fetches a published watchdog snapshot with schema_version validation.
 *
 * @param primaryUrl   Production URL to try first (e.g. ./data/watchdog-results.published.json).
 * @param fallbackUrl  Sample fixture URL used when primary returns 404/error.
 */
export function useWatchdogFeed(primaryUrl: string, fallbackUrl?: string): WatchdogFeedState {
  const [state, setState] = useState<WatchdogFeedState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const urls: Array<{ url: string; source: FeedSource }> = [
        { url: primaryUrl, source: 'production' },
        ...(fallbackUrl ? [{ url: fallbackUrl, source: 'fixture' as FeedSource }] : []),
      ];

      let lastError = '';
      for (const { url, source } of urls) {
        let feed: WatchdogPublished;
        try {
          const r = await fetch(url);
          if (!r.ok) {
            lastError = `HTTP ${r.status} ${r.statusText} (${url})`;
            continue; // try fallback
          }
          feed = await r.json() as WatchdogPublished;
        } catch (err) {
          lastError = String(err);
          continue;
        }

        if (cancelled) return;

        // Fail-fast on unknown schema_version (per DATA_CONTRACT.md §7)
        if (!feed.schema_version) {
          setState({ status: 'schema_mismatch', found: '(missing)', expected: WATCHDOG_SCHEMA_VERSION });
          return;
        }
        if (feed.schema_version !== WATCHDOG_SCHEMA_VERSION) {
          setState({ status: 'schema_mismatch', found: feed.schema_version, expected: WATCHDOG_SCHEMA_VERSION });
          return;
        }
        if (!Array.isArray(feed.proposals)) {
          setState({ status: 'error', message: '`proposals` pole chybí nebo není pole.' });
          return;
        }

        setState({ status: 'ok', feed, source });
        return;
      }

      if (!cancelled) setState({ status: 'error', message: lastError });
    }

    load();
    return () => { cancelled = true; };
  }, [primaryUrl, fallbackUrl]);

  return state;
}
